/**
 * White-label configuration per tenant.
 * Allows customizing colors, logos, fonts, and brand name.
 */
import { DOC_COLORS, FONTS } from '../config/documentStyles';

export interface WhiteLabelConfig {
  // Brand
  brandName: string;
  brandSubtitle: string;
  poweredBy: string;

  // Logos (URL or path)
  logoSidebar: string; // White logo for dark sidebar
  logoHeader: string; // Horizontal white logo for header
  logoColor: string; // Color logo for footer/light backgrounds
  favicon: string;

  // Colors
  colors: {
    primary: string; // Main brand color (used for buttons, links)
    primaryDark: string; // Darker shade for sidebar, header
    primaryDarker: string; // Darkest shade
    accent: string; // Accent color for highlights
    accentLight: string; // Light accent
    secondary: string; // Secondary brand color
    background: string; // Page background
    rowAlternate: string; // Table alternate row
    tableHeader: string; // Table header background

    // Status colors (overridable)
    success: string;
    warning: string;
    danger: string;
    info: string;
  };

  // Fonts
  fonts: {
    sans: string;
    serif: string;
    body: string;
    document: string;
    mono: string;
  };

  // Features toggles
  features: {
    aiAssistant: boolean;
    exportPDF: boolean;
    crossReferences: boolean;
    flowcharts: boolean;
  };
}

// Default configuration (VALOR·IA / Grupo Puentes)
const defaultConfig: WhiteLabelConfig = {
  brandName: 'V·IANet Governance',
  brandSubtitle: 'VALOR·IA Governance',
  poweredBy: 'VALOR·IA',

  logoSidebar: '/img/Imagotipo-blanco.png',
  logoHeader: '/img/Logo-horizontal-Blanco.png',
  logoColor: '/img/Logo-horizontal-Color.png',
  favicon: '/vite.svg',

  colors: {
    primary: DOC_COLORS.h2,
    primaryDark: '#16365B',
    primaryDarker: '#0E243E',
    accent: DOC_COLORS.h3,
    accentLight: '#33BCE1',
    secondary: '#1A94BF',
    background: '#ECEEF2',
    rowAlternate: '#F2F4F6',
    tableHeader: DOC_COLORS.tableHeaderBg,

    success: '#059669',
    warning: '#F59E0B',
    danger: DOC_COLORS.redChar,
    info: '#1A94BF',
  },

  fonts: {
    sans: "'Segoe UI', Arial, sans-serif",
    serif: "'Source Serif 4', Georgia, serif",
    body: "'Source Sans 3', 'Segoe UI', sans-serif",
    document: FONTS.normative,
    mono: FONTS.mono,
  },

  features: {
    aiAssistant: true,
    exportPDF: true,
    crossReferences: true,
    flowcharts: true,
  },
};

// Tenant-specific overrides (add more tenants here)
const tenantConfigs: Record<string, Partial<WhiteLabelConfig>> = {
  // Example: another company
  // 'acme-corp': {
  //   brandName: 'ACME Governance',
  //   brandSubtitle: 'ACME Corp',
  //   poweredBy: 'ACME',
  //   colors: {
  //     ...defaultConfig.colors,
  //     primary: '#E11D48',
  //     primaryDark: '#9F1239',
  //     primaryDarker: '#4C0519',
  //   },
  // },
};

let currentConfig: WhiteLabelConfig = { ...defaultConfig };

/**
 * Initialize the white-label config for the current tenant.
 * Call this once at app startup.
 */
export function initWhiteLabel(tenantSlug?: string): WhiteLabelConfig {
  if (tenantSlug && tenantConfigs[tenantSlug]) {
    const override = tenantConfigs[tenantSlug];
    currentConfig = {
      ...defaultConfig,
      ...override,
      colors: { ...defaultConfig.colors, ...override.colors },
      fonts: { ...defaultConfig.fonts, ...override.fonts },
      features: { ...defaultConfig.features, ...override.features },
    };
  } else {
    currentConfig = { ...defaultConfig };
  }

  // Apply CSS custom properties to :root
  applyTheme(currentConfig);

  return currentConfig;
}

/**
 * Update the white-label config from tenant API data.
 * Called by TenantProvider when tenant data is loaded from the backend.
 */
export function updateWhiteLabelFromTenant(tenantData: {
  nombreMarca?: string | null;
  logoUrl?: string | null;
  logoHeaderUrl?: string | null;
  faviconUrl?: string | null;
  colorPrimario?: string;
  colorSecundario?: string;
  colorAcento?: string;
  fuente?: string;
}): WhiteLabelConfig {
  currentConfig = {
    ...currentConfig,
    brandName: tenantData.nombreMarca ?? currentConfig.brandName,
    logoHeader: tenantData.logoHeaderUrl ?? currentConfig.logoHeader,
    logoColor: tenantData.logoUrl ?? currentConfig.logoColor,
    favicon: tenantData.faviconUrl ?? currentConfig.favicon,
    colors: {
      ...currentConfig.colors,
      primary: tenantData.colorSecundario ?? currentConfig.colors.primary,
      primaryDarker: tenantData.colorPrimario ?? currentConfig.colors.primaryDarker,
      accent: tenantData.colorAcento ?? currentConfig.colors.accent,
    },
  };

  applyTheme(currentConfig);
  return currentConfig;
}

/**
 * Get the current white-label config.
 */
export function getWhiteLabel(): WhiteLabelConfig {
  return currentConfig;
}

/**
 * Apply theme colors/fonts as CSS custom properties on :root.
 */
function applyTheme(config: WhiteLabelConfig) {
  const root = document.documentElement;
  const { colors, fonts } = config;

  // Colors
  root.style.setProperty('--wl-primary', colors.primary);
  root.style.setProperty('--wl-primary-dark', colors.primaryDark);
  root.style.setProperty('--wl-primary-darker', colors.primaryDarker);
  root.style.setProperty('--wl-accent', colors.accent);
  root.style.setProperty('--wl-accent-light', colors.accentLight);
  root.style.setProperty('--wl-secondary', colors.secondary);
  root.style.setProperty('--wl-background', colors.background);
  root.style.setProperty('--wl-row-alt', colors.rowAlternate);
  root.style.setProperty('--wl-table-header', colors.tableHeader);
  root.style.setProperty('--wl-success', colors.success);
  root.style.setProperty('--wl-warning', colors.warning);
  root.style.setProperty('--wl-danger', colors.danger);
  root.style.setProperty('--wl-info', colors.info);

  // Fonts
  root.style.setProperty('--wl-font-sans', fonts.sans);
  root.style.setProperty('--wl-font-serif', fonts.serif);
  root.style.setProperty('--wl-font-body', fonts.body);
  root.style.setProperty('--wl-font-doc', fonts.document);
  root.style.setProperty('--wl-font-mono', fonts.mono);

  // Update page title — siempre V·IAnet (nombre del producto, independiente del white-label del tenant)
  document.title = 'V·IAnet';
}
